import { useCallback, useEffect, useMemo, useState } from 'react';
import { actionClick } from '@lingoda/analytics';
import { sectionNameSelector, userSelector } from '@lingoda/auth';
import { type ContentBlock, PurchaseType } from '@lingoda/monolith-graphql';
import { getPurchaseSummary } from '@lingoda/purchases';
import {
    type PaymentFormData,
    PaymentMethodForm,
    PaymentMethodType,
    PaymentPurpose,
    type WithPaymentProps,
    useMaxPaymentMethods,
    usePaymentMethodType,
    withPayment,
} from '@lingoda/payments';
import { trans } from '@lingoda/i18n';
import { Alert, Divider, Stack, useUiEvent } from '@lingoda/ui';
import { useSelector } from '@lingoda/hooks';
import type { FormBag, FormErrors, FormProps } from '@lingoda/forms';
import { Form, withFormWrapper } from '@lingoda/forms';
import { getTransactionType } from '../utils';
import { validate } from './schema';
import TermsAndConditionsSection from './TermsAndConditionsSection';
import SectionWrapper from './SectionWrapper';
import ConfirmSection from './ConfirmSection';
import TestChargeWarningSection from './TestChargeWarningSection';
import { type FormValues } from './schema';
import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import type { CheckoutPurchase } from '../types';

export type OwnProps = {
    purchase: CheckoutPurchase;
    serverErrors?: FormErrors<PaymentFormData>;
    chargeAmount?: number;
    asyncValidating: boolean;
    submitLabel: string;
    terms?: ContentBlock[];
    testChargeLabel?: string;
    isSubscriptionChange?: boolean;
};

interface Props extends OwnProps, WithPaymentProps, FormProps<FormValues> {}

const PaymentForm = ({
    values,
    serverErrors,
    purchase,
    isValid,
    setFieldValue,
    asyncValidating,
    submitLabel,
    terms,
    testChargeLabel,
    children,
    isSubscriptionChange,
    ...props
}: PropsWithChildren<Props>) => {
    const addEvent = useUiEvent();
    const [validPaymentMethod, setValidPaymentMethod] = useState(false);
    const section = useSelector(sectionNameSelector);
    const user = useSelector(userSelector);
    const { maxPaymentMethods, isLimitReached: maxPaymentMethodsReached } = useMaxPaymentMethods();
    const { paymentMethod, country, name } = values;
    const type = usePaymentMethodType(paymentMethod);
    const { initPaymentRequest, setErrors, updatePaymentRequest, validating } = props;
    const loading = validating || asyncValidating;
    const isPaymentRequest = type === PaymentMethodType.PaymentRequest;
    const { amount, currency } = purchase.chargeTotal;

    const onPay = useCallback(() => {
        void addEvent(actionClick('ConfirmPaymentButton'), {
            metadata: {
                paymentMethod: type,
                groupCredits: purchase.groupCredits,
                privateCredits: purchase.privateCredits,
                productType: purchase.type,
                transactionType: getTransactionType(purchase, isSubscriptionChange),
            },
        });
    }, [addEvent, type, purchase, isSubscriptionChange]);

    const purchaseSummary = useMemo(() => getPurchaseSummary(purchase), [purchase]);

    useEffect(() => {
        void setFieldValue('paymentMethodType', type);
    }, [type, setFieldValue]);

    useEffect(() => {
        void setFieldValue('section', section);
    }, [section, setFieldValue]);

    useEffect(() => {
        void setFieldValue('email', user?.email);
    }, [user?.email, setFieldValue]);

    useEffect(() => {
        if (purchase.agreements) {
            void setFieldValue(
                'agreements',
                purchase.agreements.reduce((acc, { id }) => ({ ...acc, [id]: false }), {}),
            );
        }
    }, [purchase.agreements, setFieldValue]);

    useEffect(() => {
        if (serverErrors) {
            setErrors(serverErrors);
        }
    }, [serverErrors, setErrors]);

    useEffect(() => {
        if (isPaymentRequest && section) {
            initPaymentRequest({
                amount,
                section,
                currency,
                label: purchaseSummary,
            });
        }
    }, [isPaymentRequest, purchaseSummary, currency, amount, section, initPaymentRequest]);

    useEffect(() => {
        updatePaymentRequest({
            amount,
            currency,
            label: purchaseSummary,
        });
    }, [currency, purchaseSummary, amount, updatePaymentRequest]);

    useEffect(() => {
        const value = type && validPaymentMethod;
        if (value !== values.validPaymentMethod) {
            void setFieldValue('validPaymentMethod', value);
        }
    }, [setFieldValue, type, validPaymentMethod, values.validPaymentMethod]);

    return (
        <Form>
            <Stack spacing={{ xs: 3, sm: 4 }}>
                {children}
                <SectionWrapper>
                    {maxPaymentMethodsReached && (
                        <Alert severity="info">
                            {trans(
                                'max-payment-methods-reached',
                                { limit: maxPaymentMethods },
                                'student-common+intl-icu',
                            )}
                        </Alert>
                    )}
                    <PaymentMethodForm
                        setError={props.setErrorPayment}
                        isValidCallback={setValidPaymentMethod}
                        currency={currency}
                        type={type}
                        errorMessage={props.errorPayment}
                        paymentMethod={paymentMethod}
                        country={country}
                        name={name}
                    />
                    {type !== PaymentMethodType.Sofort && testChargeLabel && (
                        <TestChargeWarningSection testChargeLabel={testChargeLabel} />
                    )}
                </SectionWrapper>
                <Divider />
                <SectionWrapper>
                    <TermsAndConditionsSection purchase={purchase} terms={terms} />
                </SectionWrapper>
                <ConfirmSection
                    loading={loading}
                    isValid={isValid}
                    onClick={onPay}
                    submitLabel={submitLabel}
                />
            </Stack>
        </Form>
    );
};

const PaymentFormDecorated = withPayment<OwnProps>(
    withFormWrapper<Props, FormValues>({
        mapPropsToValues: () =>
            ({
                paymentMethod: undefined,
                marketing: undefined,
                paymentMethodType: undefined,
                validPaymentMethod: false,
                termsAndConditions: undefined,
                ageAboveEighteen: undefined,
            }) as FormValues,
        validate,
        handleSubmit: (
            values: FormValues,
            { props: { setErrorPayment, submitPayment, purchase } }: FormBag<Props, FormValues>,
        ) => {
            if (!values.section) {
                return;
            }

            setErrorPayment(undefined);

            submitPayment(
                {
                    email: values.email,
                    section: values.section,
                    name: values.name,
                    country: values.country,
                    paymentMethod: {
                        type: values.paymentMethodType,
                        value: values.paymentMethod,
                    },
                },
                values,
                {
                    paymentPurpose:
                        purchase.type === PurchaseType.ExtraCredits
                            ? PaymentPurpose.extraCredits
                            : PaymentPurpose.subscription,
                },
            );
        },
    })(PaymentForm) as React.ComponentType<OwnProps>,
);

export type CheckoutFormProps = ComponentPropsWithoutRef<typeof PaymentFormDecorated>;
export default PaymentFormDecorated;
